/**
 * Un ZIP sin comprimir, escrito y leído a mano.
 *
 * Tres decisiones, y las tres por el mismo motivo: lo que hay dentro son fotos
 * de los niños haciendo sus tareas, y un archivo que solo abre la app que lo
 * escribió es un archivo rehén.
 *
 * **ZIP y no un formato propio**, porque se abre con doble clic en cualquier
 * ordenador. Si esta app desaparece, las fotos siguen siendo fotos.
 *
 * **Sin comprimir** (método 0, «stored»). Un JPEG ya está comprimido; pasarlo
 * por deflate ahorra un uno por ciento y obliga a llevar el comprimor dentro.
 * Al leer sí se acepta deflate, porque una carpeta que alguien haya vuelto a
 * comprimir desde el Finder llega así.
 *
 * **Sin dependencias**, que es la razón por la que está aquí en vez de traer una
 * librería de zip: son unas cien líneas de cabeceras con posiciones fijas, y a
 * cambio esta app sigue teniendo cuatro dependencias.
 *
 * El detalle que importa en una tablet: los bytes de cada foto se leen una vez
 * para el CRC y se sueltan. En el `Blob` final va el `Blob` original, que lo
 * respalda IndexedDB en disco y no la memoria. Un archivo de doscientas fotos no
 * se monta entero en RAM.
 */

/**
 * Bytes que pueden ir dentro de un `Blob`.
 *
 * `Uint8Array` a secas incluye también memoria compartida entre hilos, que un
 * `Blob` no acepta. Decirlo una vez aquí evita repetir el tipo largo en cada
 * firma, y sobre todo evita la tentación de callar al comprobador con una
 * aserción.
 */
type Bytes = Uint8Array<ArrayBuffer>

export interface ZipEntry {
  /** Ruta dentro del archivo, con `/`. */
  readonly name: string
  readonly blob: Blob
  /** Fecha que verá quien abra el archivo. Sin ella, la de hoy. */
  readonly at?: string
}

const LOCAL = 0x04034b50
const CENTRAL = 0x02014b50
const EOCD = 0x06054b50
const STORED = 0
const DEFLATE = 8
/** Sin zip64 no se puede pasar de aquí, ni en un archivo ni en una entrada. */
const MAX_BYTES = 0xffff_ffff

let tabla: Uint32Array | null = null

function tablaCrc(): Uint32Array {
  if (tabla) return tabla
  const nueva = new Uint32Array(256)
  for (let i = 0; i < 256; i += 1) {
    let c = i
    for (let bit = 0; bit < 8; bit += 1) {
      c = c & 1 ? 0xedb8_8320 ^ (c >>> 1) : c >>> 1
    }
    nueva[i] = c >>> 0
  }
  tabla = nueva
  return nueva
}

/** CRC-32 tal y como lo pide el formato: el mismo que comprueba `unzip -t`. */
export function crc32(bytes: Bytes): number {
  const t = tablaCrc()
  let c = 0xffff_ffff
  for (const byte of bytes) {
    c = (t[(c ^ byte) & 0xff] ?? 0) ^ (c >>> 8)
  }
  return (c ^ 0xffff_ffff) >>> 0
}

/**
 * La hora en el formato de MS-DOS, que es lo que lleva un ZIP: la fecha en
 * dieciséis bits y la hora en otros dieciséis, con los segundos a saltos de dos.
 * Antes de 1980 no hay nada que representar, así que se queda en 1980.
 */
function fechaDos(iso: string | undefined): { time: number; date: number } {
  const d = iso === undefined ? new Date() : new Date(iso)
  const valida = Number.isNaN(d.getTime()) ? new Date() : d
  const año = Math.max(1980, valida.getUTCFullYear())
  return {
    time:
      (valida.getUTCHours() << 11) | (valida.getUTCMinutes() << 5) | (valida.getUTCSeconds() >> 1),
    date: ((año - 1980) << 9) | ((valida.getUTCMonth() + 1) << 5) | valida.getUTCDate(),
  }
}

/** Escribe un ZIP sin comprimir. */
export async function zipStore(entries: readonly ZipEntry[]): Promise<Blob> {
  const codificar = new TextEncoder()
  const partes: BlobPart[] = []
  const directorio: Bytes[] = []
  let offset = 0

  for (const entry of entries) {
    const nombre = codificar.encode(entry.name)
    // Se leen los bytes solo para el CRC; al salir del bucle se sueltan y en el
    // archivo va el `Blob` de origen.
    const bytes = new Uint8Array(await entry.blob.arrayBuffer())
    const tamaño = bytes.byteLength
    if (tamaño > MAX_BYTES) {
      throw new RangeError(`«${entry.name}» no cabe en un zip sin zip64.`)
    }
    const crc = crc32(bytes)
    const { time, date } = fechaDos(entry.at)

    const local = new Uint8Array(30 + nombre.byteLength)
    const cabecera = new DataView(local.buffer)
    cabecera.setUint32(0, LOCAL, true)
    cabecera.setUint16(4, 20, true) // versión necesaria para extraer: 2.0
    cabecera.setUint16(6, 0, true) // sin banderas
    cabecera.setUint16(8, STORED, true)
    cabecera.setUint16(10, time, true)
    cabecera.setUint16(12, date, true)
    cabecera.setUint32(14, crc, true)
    cabecera.setUint32(18, tamaño, true) // comprimido
    cabecera.setUint32(22, tamaño, true) // sin comprimir: el mismo
    cabecera.setUint16(26, nombre.byteLength, true)
    cabecera.setUint16(28, 0, true) // sin campo extra
    local.set(nombre, 30)

    const central = new Uint8Array(46 + nombre.byteLength)
    const ficha = new DataView(central.buffer)
    ficha.setUint32(0, CENTRAL, true)
    ficha.setUint16(4, 20, true) // versión que lo escribió
    ficha.setUint16(6, 20, true) // versión necesaria
    ficha.setUint16(8, 0, true)
    ficha.setUint16(10, STORED, true)
    ficha.setUint16(12, time, true)
    ficha.setUint16(14, date, true)
    ficha.setUint32(16, crc, true)
    ficha.setUint32(20, tamaño, true)
    ficha.setUint32(24, tamaño, true)
    ficha.setUint16(28, nombre.byteLength, true)
    ficha.setUint16(30, 0, true) // extra
    ficha.setUint16(32, 0, true) // comentario
    ficha.setUint16(34, 0, true) // disco
    ficha.setUint16(36, 0, true) // atributos internos
    ficha.setUint32(38, 0, true) // atributos externos
    ficha.setUint32(42, offset, true)
    central.set(nombre, 46)

    partes.push(local, entry.blob)
    directorio.push(central)
    offset += local.byteLength + tamaño
    if (offset > MAX_BYTES) throw new RangeError('El archivo pasa de cuatro gigas.')
  }

  const tamañoDirectorio = directorio.reduce((total, d) => total + d.byteLength, 0)
  const fin = new Uint8Array(22)
  const cierre = new DataView(fin.buffer)
  cierre.setUint32(0, EOCD, true)
  cierre.setUint16(4, 0, true)
  cierre.setUint16(6, 0, true)
  cierre.setUint16(8, entries.length, true)
  cierre.setUint16(10, entries.length, true)
  cierre.setUint32(12, tamañoDirectorio, true)
  cierre.setUint32(16, offset, true)
  cierre.setUint16(20, 0, true)

  return new Blob([...partes, ...directorio, fin], { type: 'application/zip' })
}

export interface ZipLeido {
  readonly name: string
  readonly bytes: Bytes
}

/** Lo que puede salir mal al leer un archivo que ha elegido una persona. */
export class ZipIlegible extends Error {}

async function inflar(bytes: Bytes): Promise<Bytes> {
  // `deflate-raw` es lo que lleva un ZIP: sin la envoltura de zlib.
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/**
 * Lee un ZIP. Al contrario que al escribir, aquí sí entra todo en memoria: el
 * archivo lo ha elegido una persona y lo normal es que sea el que hizo la app,
 * de unos megas. A cambio el lector es la mitad de código.
 *
 * Comprueba el CRC de cada entrada. Un archivo cortado a medias —la descarga que
 * se quedó sin batería— tiene que dar un error que se lea, no medias fotos.
 */
export async function unzipStore(file: Blob): Promise<readonly ZipLeido[]> {
  const todo = new Uint8Array(await file.arrayBuffer())
  const vista = new DataView(todo.buffer, todo.byteOffset, todo.byteLength)

  // El cierre está al final, pero puede llevar un comentario detrás de hasta
  // 65535 bytes, así que se busca hacia atrás en vez de mirar los últimos 22.
  let cierre = -1
  for (let i = todo.byteLength - 22; i >= 0 && i >= todo.byteLength - 65_557; i -= 1) {
    if (vista.getUint32(i, true) === EOCD) {
      cierre = i
      break
    }
  }
  if (cierre < 0) throw new ZipIlegible('Ese archivo no es un zip.')

  const cuantas = vista.getUint16(cierre + 10, true)
  let puntero = vista.getUint32(cierre + 16, true)
  const leidas: ZipLeido[] = []
  const decodificar = new TextDecoder()

  for (let i = 0; i < cuantas; i += 1) {
    if (puntero + 46 > todo.byteLength || vista.getUint32(puntero, true) !== CENTRAL) {
      throw new ZipIlegible('El índice del zip está roto.')
    }
    const metodo = vista.getUint16(puntero + 10, true)
    const crc = vista.getUint32(puntero + 16, true)
    const comprimido = vista.getUint32(puntero + 20, true)
    const largoNombre = vista.getUint16(puntero + 28, true)
    const largoExtra = vista.getUint16(puntero + 30, true)
    const largoComentario = vista.getUint16(puntero + 32, true)
    const dondeEmpieza = vista.getUint32(puntero + 42, true)
    const name = decodificar.decode(todo.subarray(puntero + 46, puntero + 46 + largoNombre))
    puntero += 46 + largoNombre + largoExtra + largoComentario

    if (dondeEmpieza + 30 > todo.byteLength || vista.getUint32(dondeEmpieza, true) !== LOCAL) {
      throw new ZipIlegible(`No encuentro «${name}» dentro del archivo.`)
    }
    // Los largos del nombre y del extra se leen otra vez de la cabecera local:
    // no tienen por qué medir lo mismo que en el índice.
    const saltar =
      30 + vista.getUint16(dondeEmpieza + 26, true) + vista.getUint16(dondeEmpieza + 28, true)
    const desde = dondeEmpieza + saltar
    if (desde + comprimido > todo.byteLength) {
      throw new ZipIlegible('El archivo está cortado.')
    }
    const crudos = todo.subarray(desde, desde + comprimido)

    // Una carpeta dentro del zip no lleva bytes y no es una entrada que leer.
    if (name.endsWith('/')) continue

    let bytes: Bytes
    if (metodo === STORED) {
      bytes = crudos
    } else if (metodo === DEFLATE) {
      bytes = await inflar(crudos)
    } else {
      throw new ZipIlegible(`«${name}» va comprimido de una manera que no sé leer.`)
    }

    if (crc32(bytes) !== crc) {
      throw new ZipIlegible(`«${name}» llegó dañado: no cuadran las cuentas.`)
    }
    leidas.push({ name, bytes })
  }

  return leidas
}
